import { GitCommit, Bot, User, FilePlus, FileEdit, FileMinus } from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { Commit } from '../../types';
import clsx from 'clsx';

interface CommitItemProps {
  commit: Commit;
  selected?: boolean;
  onClick?: () => void;
  showFiles?: boolean;
}

export function CommitItem({
  commit,
  selected,
  onClick,
  showFiles = false,
}: CommitItemProps) {
  const isAgent = commit.author.type === 'agent';
  const shortSha = commit.sha.substring(0, 7);
  const date = new Date(commit.date);

  const additions = commit.files.reduce((sum, f) => sum + f.additions, 0);
  const deletions = commit.files.reduce((sum, f) => sum + f.deletions, 0);

  return (
    <div
      className={clsx(
        'px-4 py-3 border-l-2 hover:bg-gray-50 cursor-pointer',
        selected ? 'border-primary-500 bg-primary-50' : 'border-gray-200'
      )}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        <div
          className={clsx(
            'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0',
            isAgent ? 'bg-purple-100' : 'bg-gray-100'
          )}
        >
          {isAgent ? (
            <Bot className="w-4 h-4 text-purple-600" />
          ) : (
            <User className="w-4 h-4 text-gray-600" />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">
            {commit.message.split('\n')[0]}
          </p>

          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
            <span className="font-mono bg-gray-100 px-1.5 py-0.5 rounded">
              {shortSha}
            </span>
            <span>{commit.author.name}</span>
            <span title={format(date, 'PPpp')}>
              {formatDistanceToNow(date, { addSuffix: true })}
            </span>
          </div>

          <div className="flex items-center gap-3 mt-2 text-xs">
            <span className="text-green-600">+{additions}</span>
            <span className="text-red-600">-{deletions}</span>
            <span className="text-gray-500">
              {commit.files.length} file{commit.files.length !== 1 ? 's' : ''}
            </span>
          </div>

          {showFiles && commit.files.length > 0 && (
            <div className="mt-3 space-y-1">
              {commit.files.map((file) => (
                <div
                  key={file.path}
                  className="flex items-center gap-2 text-xs text-gray-600"
                >
                  {file.status === 'added' && (
                    <FilePlus className="w-3 h-3 text-green-500" />
                  )}
                  {file.status === 'modified' && (
                    <FileEdit className="w-3 h-3 text-yellow-500" />
                  )}
                  {file.status === 'deleted' && (
                    <FileMinus className="w-3 h-3 text-red-500" />
                  )}
                  <span className="truncate">{file.path}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
